import { Button } from "@/components/ui/Button";
import { formatEmployeeTimeProjectNumber } from "@/lib/auftragAutocomplete";
import {
  formatAbsenceDateRange,
  formatAbsenceDays,
  getAbsenceTypeLabel,
  isUnavailabilityNotice,
  type EmployeeAbsenceRequest,
} from "@/lib/employeeAbsences";
import { formatHoursHm, type TimeComplianceViolation } from "@/lib/employeeTimeCompliance";
import {
  formatHours,
  type EmployeeTimeDayApproval,
  type EmployeeTimeDayApprovalProject,
} from "@/lib/employeeTimes";

function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

function formatDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDayApprovalProjectLine(project: EmployeeTimeDayApprovalProject): string {
  const number = formatEmployeeTimeProjectNumber(project);
  const parts = [
    number || null,
    project.beschreibung?.trim() || null,
    project.kunde?.trim() || null,
    project.ort?.trim() || null,
  ].filter(Boolean);
  if (parts.length === 0) return "Ohne Projekt";
  return parts.join(" · ");
}

export function AbsenceApprovalCard({
  item,
  comment,
  onCommentChange,
  busy,
  onApprove,
  onReject,
  onAcknowledge,
  compact = false,
}: {
  item: EmployeeAbsenceRequest;
  comment: string;
  onCommentChange: (value: string) => void;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
  onAcknowledge?: () => void;
  compact?: boolean;
}) {
  if (isUnavailabilityNotice(item.absence_type)) {
    return (
      <div className={compact ? "eh-home-queue-card" : "rounded-ios border border-separator bg-card p-4 shadow-ios"}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-semibold text-label">{item.user_display || item.user_login}</div>
            <div className="text-sm text-label-secondary">
              Nicht verfügbar · {formatAbsenceDays(item.total_days)}
            </div>
            <div className="mt-1 text-sm text-label">{formatAbsenceDateRange(item)}</div>
            {item.note ? <div className="mt-2 text-sm text-label-secondary">{item.note}</div> : null}
          </div>
          <span className="rounded-full bg-fill-tertiary px-2.5 py-1 text-xs font-semibold text-label-secondary">
            Info
          </span>
        </div>
        <p className="mt-3 text-sm text-label-secondary">
          Hinweis für den Montageplan. Die Schraffur ist schon sichtbar, eine Freigabe ist nicht nötig.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" disabled={busy || !onAcknowledge} onClick={onAcknowledge}>
            Als gelesen markieren
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? "eh-home-queue-card" : "rounded-ios border border-separator bg-card p-4 shadow-ios"}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-label">{item.user_display || item.user_login}</div>
          <div className="text-sm text-label-secondary">
            {getAbsenceTypeLabel(item.absence_type)} · {formatAbsenceDays(item.total_days)}
          </div>
          <div className="mt-1 text-sm text-label">{formatAbsenceDateRange(item)}</div>
          {item.note ? <div className="mt-2 text-sm text-label-secondary">{item.note}</div> : null}
        </div>
        <span className="rounded-full bg-status-orange/15 px-2.5 py-1 text-xs font-semibold text-status-orange">
          Antrag ausstehend
        </span>
      </div>

      <label className="mt-3 block space-y-1">
        <span className="eh-label">Kommentar (optional)</span>
        <input
          type="text"
          className="eh-input w-full"
          placeholder="Optional"
          value={comment}
          onChange={(e) => onCommentChange(e.target.value)}
        />
      </label>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="primary" size="sm" disabled={busy} onClick={onApprove}>
          Genehmigen
        </Button>
        <Button variant="secondary" size="sm" disabled={busy} onClick={onReject}>
          Ablehnen
        </Button>
      </div>
    </div>
  );
}

function violationTitle(item: EmployeeTimeDayApproval): string {
  const first = (item.violations as TimeComplianceViolation[])[0];
  return first?.title || first?.code || "Arbeitszeitverstoß";
}

function statusBadge(item: EmployeeTimeDayApproval): {
  label: string;
  className: string;
} {
  if (item.has_justification) {
    return {
      label: "Begründung hinterlegt",
      className: "bg-status-red/15 text-status-red",
    };
  }
  if (item.historical_gf_review && item.status === "approved") {
    return {
      label: "Historische GF-Freigabe",
      className: "bg-status-green/15 text-status-green",
    };
  }
  if (item.historical_gf_review && item.status === "rejected") {
    return {
      label: "Historisch abgelehnt",
      className: "bg-fill-tertiary text-label-secondary",
    };
  }
  return {
    label: "Begründung ausstehend",
    className: "bg-status-orange/15 text-status-orange",
  };
}

/** Read-only Karte für Arbeitszeitverstöße (keine GF-Freigabe mehr). */
export function DayViolationCard({
  item,
  compact = false,
}: {
  item: EmployeeTimeDayApproval;
  compact?: boolean;
}) {
  const projects = item.projects ?? [];
  const badge = statusBadge(item);
  const requiredBreak = (item.violations as TimeComplianceViolation[]).find(
    (v) => v.required_break_minutes != null,
  )?.required_break_minutes;
  const submittedAt = formatDateTime(
    item.justification_updated_at || item.justification_created_at,
  );

  return (
    <div
      className={`rounded-ios border border-separator bg-card shadow-ios ${compact ? "p-3" : "p-4"}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-label">{item.user_display || item.user_login}</div>
          <div className="text-sm text-label-secondary">
            {formatDate(item.work_date)} · {item.user_login}
          </div>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${badge.className}`}>
          {badge.label}
        </span>
      </div>

      <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-label-secondary">Arbeitszeitverstoß</dt>
          <dd className="font-medium text-label">{violationTitle(item)}</dd>
        </div>
        <div>
          <dt className="text-label-secondary">Datum</dt>
          <dd className="font-medium text-label">{formatDate(item.work_date)}</dd>
        </div>
        {item.work_hours != null ? (
          <div>
            <dt className="text-label-secondary">Arbeitszeit</dt>
            <dd className="font-medium text-label">
              {formatHoursHm(Number(item.work_hours))} Stunden
            </dd>
          </div>
        ) : null}
        {item.break_minutes != null ? (
          <div>
            <dt className="text-label-secondary">Erfasste Pause</dt>
            <dd className="font-medium text-label">
              {formatHoursHm(Number(item.break_minutes) / 60)} Stunden
            </dd>
          </div>
        ) : null}
        {requiredBreak != null ? (
          <div>
            <dt className="text-label-secondary">Erforderliche Pause</dt>
            <dd className="font-medium text-label">
              {formatHoursHm(requiredBreak / 60)} Stunden
            </dd>
          </div>
        ) : null}
        <div>
          <dt className="text-label-secondary">Status</dt>
          <dd className="font-medium text-label">
            {item.has_justification
              ? "Arbeitszeitverstoß dokumentiert"
              : item.requires_justification
                ? "Begründung ausstehend"
                : badge.label}
          </dd>
        </div>
      </dl>

      <ul className="mt-3 space-y-1.5">
        {(item.violations as TimeComplianceViolation[]).map((v) => (
          <li key={v.code} className="text-sm text-label">
            {v.message}
          </li>
        ))}
      </ul>

      <div className="mt-3">
        <div className="eh-label mb-1">Begründung des Mitarbeiters</div>
        {item.employee_justification ? (
          <p className="whitespace-pre-wrap text-sm text-label">{item.employee_justification}</p>
        ) : (
          <p className="text-sm text-status-orange">Noch keine Begründung hinterlegt.</p>
        )}
        {submittedAt ? (
          <p className="mt-1 text-xs text-label-secondary">
            Begründung eingereicht am: {submittedAt} Uhr
          </p>
        ) : null}
      </div>

      {item.historical_gf_review ? (
        <div className="mt-3 rounded-md border border-separator bg-fill-tertiary/40 px-3 py-2 text-sm text-label-secondary">
          Historische GF-Daten:{" "}
          {item.status === "approved" ? "freigegeben" : item.status === "rejected" ? "abgelehnt" : item.status}
          {item.reviewed_by_display ? ` von ${item.reviewed_by_display}` : ""}
          {item.reviewed_at
            ? ` am ${new Date(item.reviewed_at).toLocaleString("de-DE")}`
            : ""}
          {item.review_comment ? ` · ${item.review_comment}` : ""}. Keine neue Freigabe möglich.
        </div>
      ) : null}

      <div className="mt-3">
        <div className="eh-label mb-1">Projekte</div>
        {projects.length === 0 ? (
          <p className="text-sm text-label-secondary">Keine Projekte an diesem Tag gebucht.</p>
        ) : (
          <ul className="space-y-1">
            {projects.map((project, idx) => (
              <li
                key={`${item.id}-${project.auftrag_id ?? "none"}-${idx}`}
                className="flex flex-wrap items-baseline justify-between gap-2 text-sm"
              >
                <span className="text-label">{formatDayApprovalProjectLine(project)}</span>
                <span className="tabular-nums text-label-secondary">{formatHours(project.hours)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** @deprecated Bitte DayViolationCard verwenden – Freigabe-Aktionen entfallen. */
export function DayApprovalCard(props: {
  item: EmployeeTimeDayApproval;
  comment?: string;
  onCommentChange?: (value: string) => void;
  busy?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
  compact?: boolean;
}) {
  return <DayViolationCard item={props.item} compact={props.compact} />;
}
